import React, { useMemo, useState } from 'react';
import T from 'prop-types';
import cx from 'classnames';

import Reports from './Reports';
import ModalTicker from './ModalTicker';
import ReportsSkeleton from './ReportsSkeleton';
import Container from '../../../../components/Container';
import Button from '../../../../components/Buttons/Button';
import Pagination from '../../../../components/Pagination';
import SimpleDropdown from '../../../../components/Dropdowns/SimpleDropdown';
import ErrorStateWithReload from '../../../../components/ErrorStateWithReload';

import { reportTypesOptions } from '../../configs/research-library-config';

const Research = ({
  reports,
  reportsFilters,
  settings,
  tickers,
  perPage = 10,
  permissions,
  disabledLibrary,
  isLoggedUser,
  additionalsFilters,
  reportCardStyle,
  containerGutters = 32,
  containerMaxWidth,
  className,
  onPageChange,
  onTickersChange,
  onTypeChange,
}) => {
  const [openModal, setOpenModal] = useState(false);

  const { type, pagination, eventId } = reportsFilters;

  const normalizedReportTypesOptions = useMemo(() => (
    reportTypesOptions(permissions).map(item => ({
      label: item.text,
      ...item,
    }))
  ), [permissions]);

  const getReportByType = reportType => reportTypesOptions(permissions)
    .find(({ value }) => value === reportType);

  const handleTypeChange = (reportType) => {
    onTypeChange({ type: getReportByType(reportType), eventId: null });
  };

  return (
    <div className={cx('research-library', className)}>
      <Container
        gutters={containerGutters}
        disableGutters={containerGutters === 0}
        maxWidth={containerMaxWidth}
      >
        <div className={cx('d-flex research-library-content', { 'empty-state': !pagination.total_count })}>
          <div className="research-library-list">
            <div className="research-library-list-heading">
              <div className="control-level">
                <div className="filter-level">
                  <SimpleDropdown
                    selected={type.value}
                    options={normalizedReportTypesOptions}
                    variant="light"
                    menuWidth={200}
                    disabled={disabledLibrary}
                    onChange={handleTypeChange}
                  />
                  {additionalsFilters}
                </div>
                <Button variant="secondary" onClick={() => setOpenModal(true)}>
                  <i className="hicon-mail-thin" />Manage Email Notifications
                </Button>
              </div>
            </div>
            <div className="research-library-main">
              <div className="d-flex research-library-main-list show-scrollbar">
                {settings.pending && <ReportsSkeleton />}
                {settings.error && <ErrorStateWithReload />}
                {!settings.pending && !settings.error && (
                  <Reports
                    reports={reports}
                    type={type.value}
                    eventId={eventId}
                    disabledLibrary={disabledLibrary}
                    isLoggedUser={isLoggedUser}
                    permissions={permissions}
                    reportCardStyle={reportCardStyle}
                  />
                )}
              </div>
              <div className="d-flex justify-content-between align-items-center research-library-main-footer">
                {!disabledLibrary && (pagination.total_count || 0) > perPage && (
                  <Pagination
                    total={pagination.total_count}
                    perPage={perPage}
                    currentPage={pagination.page - 1}
                    className="mt-sm"
                    onPageChange={onPageChange}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </Container>
      {openModal && (
        <ModalTicker
          open={openModal}
          outsideTickers={tickers}
          changeOutsideTickers={onTickersChange}
          onClose={setOpenModal}
        />
      )}
    </div>
  );
};

Research.propTypes = {
  reports: T.array,
  reportsFilters: T.object,
  settings: T.object,
  tickers: T.array,
  perPage: T.number,
  permissions: T.object,
  disabledLibrary: T.bool,
  isLoggedUser: T.bool,
  additionalsFilters: T.node,
  reportCardStyle: T.object,
  containerGutters: T.number,
  containerMaxWidth: T.number,
  className: T.string,
  onPageChange: T.func,
  onTickersChange: T.func,
  onTypeChange: T.func,
};

export default Research;
